/**
 * Registro central del sistema de ayuda.
 *
 * Cada entrada representa un "contexto" de ayuda (típicamente una vista de la app)
 * y declara los topics disponibles en esa vista.
 *
 * Forma de cada topic:
 *   { id, titleKey, type, article?, tour? }
 *     - id        : identificador único dentro del contexto
 *     - titleKey  : clave i18n para el título mostrado en la lista del modal
 *     - type      : 'article' (modal con .vue) | 'tour' (driver.js, próxima fase)
 *     - article   : ruta relativa dentro de help/articles/<locale>/ sin extensión
 *     - tour      : id del tour (cuando type === 'tour')
 *
 * El contexto se pasa explícito vía prop `context` del <HelpButton>; no se infiere
 * del nombre de ruta para que el contrato sea estable ante refactores de routing.
 */
const registry = {
    'usuarios.lista': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosAcceso', type: 'article', article: 'shared/conceptos-acceso'},
            {id: 'lista', titleKey: 'help.topics.usuariosLista', type: 'article', article: 'usuarios/lista'},
            {id: 'crear', titleKey: 'help.topics.usuariosCrear', type: 'tour', tour: 'usuarios-crear'},
        ],
    },
    'roles.lista': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosAcceso', type: 'article', article: 'shared/conceptos-acceso'},
            {id: 'lista', titleKey: 'help.topics.rolesLista', type: 'article', article: 'roles/lista'},
            {id: 'crear', titleKey: 'help.topics.rolesCrear', type: 'tour', tour: 'roles-crear'},
        ],
    },
    'grupos.lista': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosAcceso', type: 'article', article: 'shared/conceptos-acceso'},
            {id: 'lista', titleKey: 'help.topics.gruposLista', type: 'article', article: 'grupos/lista'},
            {id: 'crear', titleKey: 'help.topics.gruposCrear', type: 'tour', tour: 'grupos-crear'},
        ],
    },
    'canales.lista': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosAcceso', type: 'article', article: 'shared/conceptos-acceso'},
            {id: 'lista', titleKey: 'help.topics.canalesLista', type: 'article', article: 'canales/lista'},
            {id: 'crear', titleKey: 'help.topics.canalesCrear', type: 'tour', tour: 'canales-crear'},
        ],
    },
    'jerarquias.lista': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosAcceso', type: 'article', article: 'shared/conceptos-acceso'},
            {id: 'lista', titleKey: 'help.topics.jerarquiasLista', type: 'article', article: 'jerarquias/lista'},
            {id: 'crear', titleKey: 'help.topics.jerarquiasCrear', type: 'tour', tour: 'jerarquias-crear'},
        ],
    },
    'reportes.lista': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosReportes', type: 'article', article: 'shared/conceptos-reportes'},
            {id: 'lista', titleKey: 'help.topics.reportesLista', type: 'article', article: 'reportes/lista'},
            {id: 'crear', titleKey: 'help.topics.reportesCrear', type: 'tour', tour: 'reportes-crear'},
        ],
    },
    'reportes.generar': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosReportes', type: 'article', article: 'shared/conceptos-reportes'},
            {id: 'guia', titleKey: 'help.topics.reportesGenerar', type: 'article', article: 'reportes/generar'},
            {id: 'tour', titleKey: 'help.topics.reportesGenerarTour', type: 'tour', tour: 'reportes-generar'},
        ],
    },
    'reportes.misConsumos': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosReportes', type: 'article', article: 'shared/conceptos-reportes'},
            {id: 'guia', titleKey: 'help.topics.reportesMisConsumos', type: 'article', article: 'reportes/mis-consumos'},
        ],
    },
    'reportes.descargaDocs': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosReportes', type: 'article', article: 'shared/conceptos-reportes'},
            {id: 'guia', titleKey: 'help.topics.reportesDescargaDocs', type: 'article', article: 'reportes/descarga-docs'},
        ],
    },
    'tareas.lista': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosTareas', type: 'article', article: 'shared/conceptos-tareas'},
            {id: 'guia', titleKey: 'help.topics.tareasLista', type: 'article', article: 'tareas/lista'},
            {id: 'tour', titleKey: 'help.topics.tareasFiltrar', type: 'tour', tour: 'tareas-filtrar'},
        ],
    },
    'tareas.kanban': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosTareas', type: 'article', article: 'shared/conceptos-tareas'},
            {id: 'guia', titleKey: 'help.topics.tareasKanban', type: 'article', article: 'tareas/kanban'},
            {id: 'tour', titleKey: 'help.topics.tareasKanbanTour', type: 'tour', tour: 'tareas-kanban'},
        ],
    },
    'tareas.tiemposAtencion': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosTareas', type: 'article', article: 'shared/conceptos-tareas'},
            {id: 'guia', titleKey: 'help.topics.tareasTiempos', type: 'article', article: 'tareas/tiempos-atencion'},
            {id: 'tour', titleKey: 'help.topics.tareasTiemposTour', type: 'tour', tour: 'tareas-tiempos'},
        ],
    },
    'data.databases': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosDatos', type: 'article', article: 'shared/conceptos-datos'},
            {id: 'guia', titleKey: 'help.topics.dataDatabases', type: 'article', article: 'data/databases'},
            {id: 'crear', titleKey: 'help.topics.dataDatabasesCrear', type: 'tour', tour: 'data-databases-crear'},
        ],
    },
    'data.catalogos': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosDatos', type: 'article', article: 'shared/conceptos-datos'},
            {id: 'guia', titleKey: 'help.topics.dataCatalogos', type: 'article', article: 'data/catalogos'},
            {id: 'crear', titleKey: 'help.topics.dataCatalogosCrear', type: 'tour', tour: 'data-catalogos-crear'},
        ],
    },
    'data.ver': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosDatos', type: 'article', article: 'shared/conceptos-datos'},
            {id: 'guia', titleKey: 'help.topics.dataVer', type: 'article', article: 'data/ver'},
        ],
    },
    'reservas.mis': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosReservas', type: 'article', article: 'shared/conceptos-reservas'},
            {id: 'guia', titleKey: 'help.topics.reservasMis', type: 'article', article: 'reservas/mis'},
        ],
    },
    'reservas.panel': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosReservas', type: 'article', article: 'shared/conceptos-reservas'},
            {id: 'guia', titleKey: 'help.topics.reservasPanel', type: 'article', article: 'reservas/panel'},
        ],
    },
    'flujos.editor': {
        topics: [
            {id: 'conceptos', titleKey: 'help.topics.conceptosFlujos', type: 'article', article: 'shared/conceptos-flujos'},
            {id: 'recorrido', titleKey: 'help.topics.flujosRecorrido', type: 'tour', tour: 'flujos-recorrido'},
            {id: 'primer-flujo', titleKey: 'help.topics.flujosPrimerFlujo', type: 'tour', tour: 'flujos-primer-flujo'},
            {id: 'configurar-formulario', titleKey: 'help.topics.flujosConfigurarFormulario', type: 'tour', tour: 'flujos-configurar-formulario'},
            {id: 'salida-html', titleKey: 'help.topics.flujosSalidaHtml', type: 'tour', tour: 'flujos-salida-html'},
            {id: 'tipos-nodos', titleKey: 'help.topics.flujosTiposNodos', type: 'article', article: 'flujos/tipos-nodos'},
            {id: 'tipos-campos', titleKey: 'help.topics.flujosTiposCampos', type: 'article', article: 'flujos/tipos-campos'},
            {id: 'subtareas-y-seleccion', titleKey: 'help.topics.flujosSubtareasSeleccion', type: 'article', article: 'flujos/subtareas-y-seleccion'},
            {id: 'variables', titleKey: 'help.topics.flujosVariables', type: 'article', article: 'flujos/variables'},
            // Los ids son el contrato de los HelpButton con topic="...": no renombrarlos sin buscarlos.
            {id: 'procesos', titleKey: 'help.topics.flujosProcesos', type: 'article', article: 'flujos/procesos'},
            {id: 'asignacion', titleKey: 'help.topics.flujosAsignacion', type: 'article', article: 'flujos/asignacion'},
            {id: 'condiciones', titleKey: 'help.topics.flujosCondiciones', type: 'article', article: 'flujos/condiciones'},
            {id: 'notificaciones', titleKey: 'help.topics.flujosNotificaciones', type: 'article', article: 'flujos/notificaciones'},
            {id: 'ocr', titleKey: 'help.topics.flujosOcr', type: 'article', article: 'flujos/ocr'},
            {id: 'correo-salida', titleKey: 'help.topics.flujosCorreoSalida', type: 'article', article: 'flujos/correo-salida'},
            {id: 'javascript', titleKey: 'help.topics.flujosJavascript', type: 'article', article: 'flujos/javascript'},
        ],
    },
}

export function getTopics(context) {
    if (!context) return []
    const entry = registry[context]
    return entry ? entry.topics : []
}

export default registry
